/* Curated Products (CJ Dropshipping) — admin control room
 * Shows all AI-curated products with the three trust badges:
 *   Confidence  ·  Success rate  ·  Refund rate
 * Highlights "🔥 Trending in CH" products in a hero row.
 * Enables one-click publish to the REAL Shopify store.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Flame, Package, TrendingUp, ShieldCheck, RotateCcw,
  Upload, ExternalLink, RefreshCw, Filter, Sparkles, Trash2,
  Send, MessageCircle, Zap,
} from "lucide-react";
import { api } from "@/lib/api";
import { SafeBoundary } from "@/components/SafeBoundary";

const chf = (n) => (n == null ? "—" : `CHF ${Number(n).toFixed(2)}`);

// CJ Dropshipping product URLs follow the pattern
// `https://cjdropshipping.com/product/-p-{PID}.html`. The `-p-` marker
// is what CJ's router uses; the slug before it is ignored. This lets
// the operator click through to see the supplier listing, images,
// reviews, shipping options, etc. in a new tab.
const cjSupplierUrl = (cjPid) =>
  cjPid ? `https://cjdropshipping.com/product/-p-${cjPid}.html` : null;


export default function CuratedPage() {
  return (
    <SafeBoundary>
      <CuratedInner />
    </SafeBoundary>
  );
}

function CuratedInner() {
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [minConfidence, setMinConfidence] = useState(0);
  const [trendingOnly, setTrendingOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [importTarget, setImportTarget] = useState(20);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get(`/suppliers/cj/curated?min_confidence=${minConfidence}&trending_only=${trendingOnly}&limit=300`),
        api.get(`/suppliers/cj/daily-import/status`),
      ]);
      setProducts(p.data.products || []);
      setMeta(s.data);
    } catch (e) {
      toast.error("Failed to load curated products");
    } finally {
      setLoading(false);
    }
  }, [minConfidence, trendingOnly]);

  useEffect(() => { load(); }, [load]);

  const trending = useMemo(() => products.filter((p) => p.trending_ch), [products]);
  const regular = useMemo(() => products.filter((p) => !p.trending_ch), [products]);

  const runImport = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/suppliers/cj/daily-import/run-now`, { target: importTarget });
      const jobId = r?.data?.job_id;
      toast.success(`Import iniciado (${importTarget} produtos)`);
      // Poll job status; when it finishes, auto-publish newly imported
      // products to the real Shopify store. Runs in background — never
      // throws to the UI (all failures are toasts).
      const pollAndPublish = async () => {
        try {
          for (let i = 0; i < 30; i++) {  // up to ~90s
            await new Promise((r2) => setTimeout(r2, 3000));
            const j = await api.get(`/suppliers/cj/import-job/${jobId}`);
            if (j?.data?.status === "done") {
              const rows = j.data?.log || [];
              await load();
              // Auto-publish only the products we just imported
              let published = 0;
              for (const row of rows) {
                if (!row?.shopify_id) continue;
                try {
                  await api.post(`/suppliers/cj/publish-to-shopify`,
                                 { shopify_id: row.shopify_id, status: "active" });
                  published += 1;
                } catch { /* skip individual failures — user can retry */ }
              }
              if (published > 0) toast.success(`${published} publicados no Shopify`);
              await load();
              return;
            }
          }
          await load();
        } catch (e) {
          console.warn("post-import polling failed", e);
        }
      };
      pollAndPublish();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import falhou");
    } finally { setBusy(false); }
  };

  const runCleanup = async () => {
    if (!window.confirm("Delete products that violate current rules (price > CHF 14.90 or blocked terms)?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/suppliers/cj/cleanup-non-compliant`, { dry_run: false });
      toast.success(`Cleanup: ${r.data.deleted} removed, ${r.data.backfilled_scores} rescored`);
      load();
    } catch { toast.error("Cleanup failed"); }
    finally { setBusy(false); }
  };

  const publishOne = async (shopify_id) => {
    setBusy(true);
    try {
      const r = await api.post(`/suppliers/cj/publish-to-shopify`, { shopify_id, status: "active" });
      if (r.data.already_published) {
        toast.info(`Already on Shopify (#${r.data.shopify_real_id})`);
      } else {
        toast.success(`Published to Shopify (#${r.data.shopify_real_id})`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="curated-page">
      {/* Header */}
      <div className="rounded-lg border border-white/10 bg-gradient-to-br from-[#0B1F1F] via-[#080808] to-[#080808] p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[color:var(--hq-cyan)]">
              <Sparkles size={12} /> AI-Curated Catalog
            </div>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl">
              Produtos Vencedores
            </h1>
            <p className="mt-2 text-white/60 text-sm max-w-2xl">
              Cada produto validado com 3 sinais:
              <strong className="text-[color:var(--hq-cyan)]"> confiança ≥ 80%</strong>,
              <strong className="text-[color:var(--hq-cyan)]"> sucesso projectado</strong>,
              <strong className="text-[color:var(--hq-cyan)]"> reembolso &lt; 10%</strong>.
              Preço máximo: <strong>CHF {meta?.rules?.max_retail_chf ?? 14.9}</strong> ·
              regra prejuízo-zero: <strong>compra sempre &lt; venda</strong>.
            </p>
          </div>
          {meta && (
            <div className="grid grid-cols-3 gap-3 text-center">
              <StatCell label="curados" value={meta.total_curated_products} testid="stat-total" />
              <StatCell label="🔥 trending" value={meta.trending_ch_count} testid="stat-trending" />
              <StatCell label="max CHF" value={meta.rules?.max_retail_chf} testid="stat-max-price" />
            </div>
          )}
        </div>
      </div>

      <OperatorAIBox onExecuted={load} />

      {/* Action bar */}
      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-mono uppercase text-white/40">Target</label>
          <input
            type="number"
            data-testid="import-target"
            value={importTarget}
            onChange={(e) => setImportTarget(parseInt(e.target.value, 10) || 20)}
            min={1} max={100}
            className="w-16 bg-black/60 border border-white/10 rounded px-2 py-1 text-xs"
          />
        </div>
        <button
          data-testid="run-import-btn"
          onClick={runImport}
          disabled={busy}
          className="flex items-center gap-2 bg-[color:var(--hq-cyan)] text-black text-xs font-mono uppercase tracking-wider px-4 py-2 rounded hover:brightness-110 disabled:opacity-50 transition-all"
        >
          <Package size={12} /> {busy ? "…" : "Trazer produtos"}
        </button>
        <button
          data-testid="cleanup-btn"
          onClick={runCleanup}
          disabled={busy}
          className="flex items-center gap-2 border border-red-500/30 text-red-300 text-xs font-mono uppercase tracking-wider px-4 py-2 rounded hover:bg-red-500/10 disabled:opacity-50 transition-all"
        >
          <Trash2 size={12} /> Limpar não-conformes
        </button>

        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter size={12} className="text-white/40" />
            <label className="text-[10px] font-mono uppercase text-white/40">
              min confidence
            </label>
            <input
              type="range" min={0} max={100} step={5}
              value={minConfidence}
              data-testid="min-confidence-slider"
              onChange={(e) => setMinConfidence(parseInt(e.target.value, 10))}
              className="w-24"
            />
            <span className="text-xs font-mono w-8 text-white/70">{minConfidence}</span>
          </div>
          <label className="flex items-center gap-2 text-[10px] font-mono uppercase text-white/40 cursor-pointer">
            <input
              type="checkbox"
              data-testid="trending-only-checkbox"
              checked={trendingOnly}
              onChange={(e) => setTrendingOnly(e.target.checked)}
            />
            🔥 trending only
          </label>
          <button
            onClick={load}
            disabled={loading}
            data-testid="reload-btn"
            className="text-white/50 hover:text-white p-1"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Trending row */}
      {trending.length > 0 && (
        <div data-testid="trending-section">
          <div className="flex items-center gap-2 mb-3">
            <Flame size={16} className="text-orange-400" />
            <h2 className="font-display text-lg">🔥 Trending na Suíça</h2>
            <span className="text-[10px] font-mono uppercase text-white/40">
              ({trending.length}) · varredura horária
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {trending.map((p) => (
              <ProductCard key={p.shopify_id} p={p} onPublish={publishOne} busy={busy} highlight />
            ))}
          </div>
        </div>
      )}

      {/* Regular grid */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Package size={14} className="text-white/60" />
          <h2 className="font-display text-lg">Catálogo curado</h2>
          <span className="text-[10px] font-mono uppercase text-white/40">
            ({regular.length}) · ordenado por confidence
          </span>
        </div>
        {loading ? (
          <div className="text-white/40 text-sm py-12 text-center">A carregar…</div>
        ) : regular.length === 0 ? (
          <div className="text-white/40 text-sm py-12 text-center">
            Sem produtos que cumpram os filtros. Baixa o slider ou dispara um novo import.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {regular.map((p) => (
              <ProductCard key={p.shopify_id} p={p} onPublish={publishOne} busy={busy} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCell({ label, value, testid }) {
  return (
    <div className="min-w-[80px] rounded border border-white/10 bg-black/40 px-3 py-2" data-testid={testid}>
      <div className="font-display text-2xl leading-none">{value ?? "—"}</div>
      <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-white/40">{label}</div>
    </div>
  );
}

function ProductCard({ p, onPublish, busy, highlight = false }) {
  // Defensive: never crash on partial data. Every field is optional.
  const safeP = p || {};
  const conf = safeP.confidence_score ?? 0;
  const succ = safeP.success_rate ?? 0;
  const refund = safeP.refund_rate ?? 0;
  const supplierUrl = cjSupplierUrl(safeP.cj_pid);
  const title = safeP.title || "Produto sem título";
  const imageSrc = typeof safeP.image === "string" ? safeP.image
                : (safeP.image && safeP.image.src) ? safeP.image.src
                : null;

  const openSupplier = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (supplierUrl) window.open(supplierUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid={`curated-product-${safeP.shopify_id}`}
      className={`rounded-lg border overflow-hidden bg-[#0a0a0a] transition-colors ${
        highlight
          ? "border-orange-500/40 hover:border-orange-400"
          : "border-white/10 hover:border-white/25"
      }`}
    >
      {/* Image — clicking opens CJ supplier page in a new tab so the
       * operator can vet quality, reviews and shipping before publishing */}
      <a
        href={supplierUrl || "#"}
        onClick={supplierUrl ? undefined : (e) => e.preventDefault()}
        target="_blank"
        rel="noopener noreferrer"
        className="block aspect-square bg-black/60 relative overflow-hidden group cursor-pointer"
        data-testid={`supplier-link-${safeP.shopify_id}`}
        title="Ver ficha do fornecedor (CJ Dropshipping) em nova aba"
      >
        {imageSrc ? (
          <img src={imageSrc} alt={title} loading="lazy"
               className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <Package size={40} />
          </div>
        )}
        {supplierUrl && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="bg-[color:var(--hq-cyan)] text-black text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded flex items-center gap-1.5">
              <ExternalLink size={11} /> Ver ficha do fornecedor
            </div>
          </div>
        )}
        {highlight && (
          <div className="absolute top-2 left-2 bg-orange-500 text-black text-[9px] font-mono uppercase px-2 py-0.5 rounded-full flex items-center gap-1">
            <Flame size={10} /> Trending CH
          </div>
        )}
        {safeP.shopify_published && (
          <div className="absolute top-2 right-2 bg-emerald-500 text-black text-[9px] font-mono uppercase px-2 py-0.5 rounded-full">
            ✓ Shopify
          </div>
        )}
      </a>

      {/* Body */}
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-medium text-white/90 leading-tight line-clamp-2 min-h-[2.6rem]">
            {supplierUrl ? (
              <a href={supplierUrl} target="_blank" rel="noopener noreferrer"
                 onClick={openSupplier}
                 className="hover:text-[color:var(--hq-cyan)] transition-colors"
                 data-testid={`supplier-title-${safeP.shopify_id}`}>
                {title}
              </a>
            ) : title}
          </h3>
        </div>
        <div className="flex items-baseline justify-between">
          <div className="font-display text-lg">{chf(safeP.price_chf)}</div>
          {safeP.category_label && (
            <div className="text-[9px] font-mono uppercase text-white/40">
              {safeP.category_label}
            </div>
          )}
        </div>

        {/* Buy vs Sell — the prejuízo-zero rule made visible */}
        {safeP.cost_usd != null && (
          <div className="flex items-center justify-between text-[10px] font-mono text-white/50 border border-white/5 rounded px-2 py-1 bg-black/30">
            <span>compra: <strong className="text-white/80">${Number(safeP.cost_usd).toFixed(2)}</strong></span>
            <span className="text-emerald-400">→</span>
            <span>venda: <strong className="text-emerald-300">{chf(safeP.price_chf)}</strong></span>
          </div>
        )}

        {/* Trust badges */}
        <div className="grid grid-cols-3 gap-1.5 text-center">
          <TrustBadge icon={ShieldCheck} label="confiança" value={`${conf}%`}
                       tone={conf >= 80 ? "green" : conf >= 60 ? "amber" : "red"} />
          <TrustBadge icon={TrendingUp} label="sucesso" value={`${succ}%`}
                       tone={succ >= 70 ? "green" : succ >= 50 ? "amber" : "red"} />
          <TrustBadge icon={RotateCcw} label="reembolso" value={`${refund}%`}
                       tone={refund <= 5 ? "green" : refund <= 10 ? "amber" : "red"} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-white/5">
          {safeP.shopify_published ? (
            <a
              href={`https://admin.shopify.com/products/${safeP.shopify_real_id}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-300 hover:text-emerald-100"
              data-testid={`view-shopify-${safeP.shopify_id}`}
            >
              Ver no Shopify <ExternalLink size={10} />
            </a>
          ) : (
            <button
              onClick={() => onPublish(safeP.shopify_id)}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-1.5 bg-[color:var(--hq-cyan)] text-black text-[10px] font-mono uppercase py-1.5 rounded hover:brightness-110 disabled:opacity-40"
              data-testid={`publish-${safeP.shopify_id}`}
            >
              <Upload size={11} /> Publicar Shopify
            </button>
          )}
          {supplierUrl && (
            <a
              href={supplierUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={openSupplier}
              className="text-white/50 hover:text-[color:var(--hq-cyan)] p-1"
              aria-label="Abrir ficha CJ"
              title="Ver ficha do fornecedor no CJ Dropshipping"
              data-testid={`supplier-btn-${safeP.shopify_id}`}
            >
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function TrustBadge({ icon: Icon, label, value, tone }) {
  const colors = {
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/25",
    red: "bg-red-500/15 text-red-300 border-red-500/25",
  };
  return (
    <div className={`rounded border px-1.5 py-1 ${colors[tone] || colors.amber}`}>
      <div className="flex items-center justify-center gap-1">
        <Icon size={9} />
        <span className="text-[10px] font-mono">{value}</span>
      </div>
      <div className="text-[8px] font-mono uppercase opacity-70">{label}</div>
    </div>
  );
}


/* ------------------- Operator AI chat box -------------------
 * Sits at the top of the Products page. The operator asks a question
 * or gives an order; GPT-5.2 returns an answer plus (optionally) one
 * or more clickable "action" buttons that hit whitelisted admin APIs.
 * All action executions run through the browser api singleton so the
 * bearer token is included automatically.
 */
function OperatorAIBox({ onExecuted }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      answer: "Olá! Sou a tua IA operadora. Pergunta-me qualquer coisa sobre a loja ou dá-me uma ordem — exemplo: «traz-me 15 produtos de bijuteria feminina» ou «quantos produtos trending temos?».",
      actions: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [executing, setExecuting] = useState({});

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", answer: msg, actions: [] }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/operator-ai/ask", { message: msg, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", answer: r.data.answer, actions: r.data.actions || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", answer: `⚠️ ${e?.response?.data?.detail || "erro"}`, actions: [] }]);
    } finally { setSending(false); }
  };

  const runAction = async (msgIdx, actionIdx, action) => {
    const key = `${msgIdx}-${actionIdx}`;
    setExecuting((e) => ({ ...e, [key]: true }));
    try {
      const method = (action.method || "POST").toUpperCase();
      const res = method === "GET"
        ? await api.get(action.endpoint)
        : await api.post(action.endpoint, action.payload || {});
      toast.success(`Executado: ${action.label}`);
      // Show the raw response as a follow-up assistant message
      setMessages((m) => [...m, {
        role: "assistant",
        answer: "✓ Ordem executada. " + JSON.stringify(res.data, null, 2).slice(0, 200) + (JSON.stringify(res.data).length > 200 ? "…" : ""),
        actions: [],
      }]);
      onExecuted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Falha: ${action.label}`);
    } finally {
      setExecuting((e) => ({ ...e, [key]: false }));
    }
  };

  return (
    <div className="rounded-lg border border-[color:var(--hq-cyan)]/30 bg-[#0a0a0a] overflow-hidden" data-testid="operator-ai-box">
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2 bg-[color:var(--hq-cyan)]/5">
        <Zap size={14} className="text-[color:var(--hq-cyan)]" />
        <div className="font-display text-sm">IA Operadora</div>
        <span className="text-[10px] font-mono uppercase text-white/40 ml-2">
          pergunta ou dá ordens · sem editar código
        </span>
      </div>
      <div className="max-h-[340px] overflow-y-auto px-5 py-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className="space-y-2" data-testid={`op-ai-msg-${i}`}>
            <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-snug whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[color:var(--hq-cyan)] text-black"
                  : "bg-black/50 border border-white/10 text-white/85"
              }`}>
                {m.answer}
              </div>
            </div>
            {m.actions && m.actions.length > 0 && (
              <div className="pl-2 flex flex-wrap gap-2">
                {m.actions.map((a, j) => (
                  <button
                    key={j}
                    data-testid={`op-ai-action-${i}-${j}`}
                    onClick={() => runAction(i, j, a)}
                    disabled={executing[`${i}-${j}`]}
                    title={a.description || ""}
                    className="text-xs font-mono uppercase tracking-wide bg-[color:var(--hq-cyan)] text-black px-3 py-1.5 rounded hover:brightness-110 disabled:opacity-40 transition-all"
                  >
                    {executing[`${i}-${j}`] ? "…" : `▶ ${a.label}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {sending && <div className="text-white/40 text-xs">a pensar…</div>}
      </div>
      <div className="border-t border-white/10 p-3 flex items-center gap-2">
        <MessageCircle size={14} className="text-white/40" />
        <input
          data-testid="op-ai-input"
          placeholder="Pergunta ou dá uma ordem… (Enter para enviar)"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          disabled={sending}
          className="flex-1 bg-black/60 border border-white/10 rounded px-3 py-2 text-sm focus:border-white/40 outline-none"
        />
        <button
          data-testid="op-ai-send-btn"
          onClick={send}
          disabled={sending || !input.trim()}
          className="w-9 h-9 flex items-center justify-center bg-[color:var(--hq-cyan)] text-black rounded hover:brightness-110 disabled:opacity-30 transition-all"
        ><Send size={14} /></button>
      </div>
    </div>
  );
}
